/**
 * Standing landing grants in the PWA (docs/e2e.md "Standing landing grants").
 *
 * On a fast-profile project the user creates ONE passkey-approved grant (Settings › Landing
 * grant). While it is active, this device's `land.merge` commands carry it instead of a fresh
 * tap: one-key land (L) and "Land all ready" run without a passkey sheet. Each daemon checks
 * the grant against its own project facts; "land anyway", releases, secrets and deletions
 * always keep the passkey step-up.
 */
import { useQuery } from '@tanstack/react-query'
import {
  createLandGrantChallenge,
  createLandGrantStatement,
  DEFAULT_LAND_GRANT_TTL_MS,
  landGrantTargetDigest,
  type WebAuthnAssertion,
} from '@viko/crypto'
import type { LandGrantProof, LandGrantRow } from '@viko/protocol'
import { useEffect, useState } from 'react'
import type { Api } from './api.js'
import { useBackend } from './backend.js'
import { loadDevice } from './device-key.js'
import { useProjects } from './queries.js'
import { isSessionOnly } from './session-only.js'
import type { Project } from './types.js'

export type { LandGrantProof, LandGrantRow }

export const landGrantKey = (projectId?: string) => ['land-grants', projectId ?? '*'] as const

/** Renew prompt window: the card nudges when fewer than this many ms remain. */
export const RENEW_WITHIN_MS = 3 * 24 * 60 * 60_000

/** The project's landing target branch as the config states it (daemon default: `main`). */
export function targetBranchOf(p: Pick<Project, 'config'>): string {
  const cfg = p.config as
    | { landing?: { targetBranch?: unknown }; repos?: { defaultBranch?: string }[] }
    | null
    | undefined
  const t = cfg?.landing?.targetBranch
  return typeof t === 'string' && t ? t : (cfg?.repos?.[0]?.defaultBranch ?? 'main')
}

/**
 * Grants are for fast or (passkey-budget WP6) balanced projects whose `land-main` is not
 * overridden to `ask`. Careful projects keep a passkey on every landing.
 */
export function grantEligible(p: Pick<Project, 'loop' | 'config'>): boolean {
  const loop = p.config?.loopProfile ?? p.loop
  return (
    (loop === 'fast' || loop === 'balanced') &&
    p.config?.autonomy?.overrides?.['land-main'] !== 'ask'
  )
}

/** This device's live grant covering `machineId` for the project, if any. */
export function activeGrantFor(
  rows: readonly LandGrantRow[] | undefined,
  o: { projectId: string; machineId?: string; deviceKeyId?: string; now: number },
): LandGrantRow | undefined {
  if (!rows || !o.deviceKeyId) return undefined
  return rows.find(
    (r) =>
      r.active &&
      !r.revokedAt &&
      r.expiresAt > o.now + 30_000 &&
      r.projectId === o.projectId &&
      r.deviceKeyId === o.deviceKeyId &&
      (!o.machineId || r.machineIds.includes(o.machineId)),
  )
}

/** Build the statement and take the one passkey tap that approves it (create or renew). */
export async function approveLandGrant(deps: {
  accountId: () => Promise<string>
  deviceKeyId: () => Promise<string>
  assert: (challenge: Uint8Array) => Promise<WebAuthnAssertion>
  now: () => number
  project: Pick<Project, 'id' | 'machines' | 'config'>
  ttlMs?: number
}): Promise<LandGrantProof> {
  if (isSessionOnly())
    throw new Error('Landing grants need a trusted device, not a session-only browser.')
  const machineIds = [...new Set(deps.project.machines)].slice(0, 8)
  if (!machineIds.length) throw new Error('This project has no machines to land on yet.')
  const statement = await createLandGrantStatement({
    accountId: await deps.accountId(),
    projectId: deps.project.id,
    targetBranch: targetBranchOf(deps.project),
    machineIds,
    deviceKeyId: await deps.deviceKeyId(),
    now: deps.now(),
    ttlMs: deps.ttlMs ?? DEFAULT_LAND_GRANT_TTL_MS,
  })
  const assertion = await deps.assert(await createLandGrantChallenge(statement))
  return { statement, assertion } as LandGrantProof
}

/** Whether a stored grant still names the project's current target branch. */
export async function grantMatchesTarget(
  row: Pick<LandGrantRow, 'projectId' | 'targetDigest'>,
  branch: string,
): Promise<boolean> {
  return row.targetDigest === (await landGrantTargetDigest(row.projectId, branch))
}

/** This device's key id (undefined until the device is trusted). */
export function useDeviceKeyId(): string | undefined {
  const [id, setId] = useState<string>()
  useEffect(() => {
    let live = true
    void loadDevice()
      .then((d) => live && setId(d?.endorsement ? d.deviceKeyId : undefined))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [])
  return id
}

export function useLandGrants(projectId?: string) {
  const b = useBackend()
  return useQuery({
    queryKey: landGrantKey(projectId),
    queryFn: () => landGrantApi(b.api).list(projectId),
    retry: false,
    staleTime: 30_000,
  })
}

/** The proof to attach to this project's landings from this device, when a grant covers them. */
export function useLandGrantFor(
  projectId: string | undefined,
  machineId: string | undefined,
): LandGrantRow | undefined {
  const grants = useLandGrants()
  const projects = useProjects()
  const deviceKeyId = useDeviceKeyId()
  if (!projectId) return undefined
  // Out of the grant's policy (profile changed since): keep the passkey path, no dead end.
  const project = projects.data?.find((p) => p.id === projectId)
  if (!project || !grantEligible(project)) return undefined
  return activeGrantFor(grants.data, {
    projectId,
    ...(machineId ? { machineId } : {}),
    ...(deviceKeyId ? { deviceKeyId } : {}),
    now: Date.now(),
  })
}

/**
 * Picker for surfaces that land many items (inbox, "Land all ready"): the project comes from
 * the item, or from the workstream via the project list (inbox items carry only workstreams).
 */
export function useLandGrantPicker(): (
  ref: { projectId?: string | null; workstreamId?: string | null },
  machineId: string | undefined,
) => LandGrantRow | undefined {
  const grants = useLandGrants()
  const projects = useProjects()
  const deviceKeyId = useDeviceKeyId()
  return (ref, machineId) => {
    const projectId =
      ref.projectId ??
      (ref.workstreamId
        ? projects.data?.find((p) => p.workstreams.some((w) => w.id === ref.workstreamId))?.id
        : undefined)
    const project = projectId ? projects.data?.find((p) => p.id === projectId) : undefined
    return projectId && project && grantEligible(project)
      ? activeGrantFor(grants.data, {
          projectId,
          ...(machineId ? { machineId } : {}),
          ...(deviceKeyId ? { deviceKeyId } : {}),
          now: Date.now(),
        })
      : undefined
  }
}

/** `SignOptions` fragment for a picked grant (none → the usual passkey step-up). */
export function landGrantOpt(row: LandGrantRow | undefined): { landGrant?: LandGrantProof } {
  return row ? { landGrant: row.proof } : {}
}

type Req = Pick<Api, 'landGrants' | 'addLandGrant' | 'revokeLandGrants'>
export function landGrantApi(api: Req) {
  return {
    list: (projectId?: string) => api.landGrants(projectId),
    add: (proof: LandGrantProof) => api.addLandGrant(proof),
    revoke: (ids: string[]) => api.revokeLandGrants(ids),
  }
}
